import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../../constants/approutes";
import surgeryicon from "../../assets/surgeryinewcon.svg";
import viewEyeIcon from "../../assets/eye.svg";
import nodata from "../../assets/nodata.svg";
import OrderDetailsModal, { SurgeryEnquiryCards } from "../shared/OrderDetailsModal";

export default function Surgery() {
  const navigate = useNavigate();
  const [surgeryOrders, setSurgeryOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchSurgeryOrders();
  }, []);

  const fetchSurgeryOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current date for the API call
      const today = new Date().toISOString().split("T")[0];
      const tenantId = localStorage.getItem("tenantId");
      const clinicId = localStorage.getItem("clinicId");
      const token = localStorage.getItem("auth_token");

      const response = await fetch(
         `${API_URL}orders?orderType=surgery&clinicId=${clinicId}&fromDate=${today}&toDate=${today}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            tenantId: tenantId,
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Handle paginated response structure
      setSurgeryOrders(data.records || []);
    } catch (err) {
      console.error("Error fetching surgery orders:", err);
      setError("Failed to load surgery orders");
      // Fallback to mock data if API fails
      setSurgeryOrders([
        {
          id: 1,
          surgerytype: "General Surgery",
          doctorname: "Dr Akansha Singh",
          status: "Scheduled",
        },
        {
          id: 2,
          surgerytype: "Orthopedic Surgery",
          doctorname: "Dr Lokesh Yadav",
          status: "Scheduled",
        },
        {
          id: 3,
          surgerytype: "General Surgery",
          doctorname: "Dr Akansha Singh",
          status: "Scheduled",
        },
        {
          id: 4,
          surgerytype: "General Surgery",
          doctorname: "Dr Akansha Singh",
          status: "Scheduled",
        },
        {
          id: 5,
          surgerytype: "General Surgery",
          doctorname: "Dr Akansha Singh",
          status: "Scheduled",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewAll = () => {
    // Navigate to clinic surgery details page
    navigate(APP_ROUTES.CLINICSURGERYDETAILS);
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setIsModalVisible(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00BD7F]"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <img src={surgeryicon} alt="Surgery Icon" className="w-10 h-10" />
          <h2 className="text-lg font-semibold text-[#004276]">
            Surgery{" "}
            <span className="bg-[#00BD7F] text-white font-medium px-2 py-1 rounded-md text-sm ml-1">
              {surgeryOrders.length}
            </span>
          </h2>
        </div>
        <button
          className="text-[#00BD7F] text-sm font-semibold hover:underline -mt-2"
          onClick={handleViewAll}
        >
          View All
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded mb-4 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-[0px_4px_8px_rgba(0,0,0,0.15)] flex-1 flex flex-col">
        {surgeryOrders.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center h-full text-gray-500 py-8"
            style={{ height: "360px" }}
          >
            <img src={nodata} alt="No Data" className="w-48 h-48 mb-4" />
            <div>No Orders available for Today's Date</div>
          </div>
        ) : (
          <div style={{ height: "360px", overflowY: "auto" }}>
            {surgeryOrders.map((order) => {
              // Get the first surgery enquiry for display
              const surgeryDetails = order.surgeryEnquiry && order.surgeryEnquiry.length > 0 
                ? order.surgeryEnquiry[0] 
                : null;
              
              return (
                <div
                  key={order.id}
                  className="flex items-center justify-between px-5 py-4 border-b last:border-none h-20"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="flex-1">
                      <p className="font-semibold text-base text-[#1A1A1A] mb-1 truncate">
                 {order.patientName || "Patient"}
                      </p>
                      <p className="text-sm text-[#004276] mb-0 truncate">
                      {surgeryDetails?.procedureName || "Surgery"}  
                      </p>
                      <p className="text-xs text-gray-500 mt-1 truncate">
                        {surgeryDetails?.hospitalName || "Hospital"}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3 items-center flex-shrink-0">
                
                    <button
                      className="bg-white border-none outline-none p-2 rounded-full flex items-center justify-center hover:bg-gray-50"
                      onClick={() => handleViewOrder(order)}
                    >
                      <img src={viewEyeIcon} alt="View" className="w-6 h-6" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {isModalVisible && selectedOrder && (
        <OrderDetailsModal
          open={isModalVisible}
          onClose={() => setIsModalVisible(false)}
          subtitle="Surgery Order Details"
          patientName={selectedOrder.patientName}
          status={selectedOrder.enquiryTickets?.[0]?.ticketStatus}
          address={selectedOrder.address}
          infoFields={[
            { label: "Order ID", value: selectedOrder.id, mono: true },
            {
              label: "Placed On",
              value: selectedOrder.createdDate?.split("T")[0],
            },
            { label: "Mobile No", value: selectedOrder.patientMobileNo },
          ]}
        >
          <SurgeryEnquiryCards surgeryEnquiry={selectedOrder.surgeryEnquiry} />
        </OrderDetailsModal>
      )}
    </div>
  );
}
